/**
 * Finance / Budget Data Adapter — the SINGLE boundary between storage and scoring.
 *
 * Mirrors kpi-engine/scheduleDataAdapter.ts but reads from the finance cost
 * localStorage layer and delegates to finance scoring functions.
 *
 * When the backend migration happens (PostgreSQL API), only this file
 * needs to change — the scoring module and chart components stay untouched.
 */

import {
  getFinanceCostSeries,
  getFinanceCostSeriesForInstance,
  getBudgetCurveMetaForInstance,
  getAllFinanceCostSeriesForProject,
  type FinanceCostPoint,
  type BudgetCurveMeta,
} from "../Data/Budget/budgetOverrides";
import {
  getProjectKpiConfigOverride,
  type SubKpiInstance,
} from "../Data/ProjectConfig/projectKpiConfigOverrides";
import type { KpiSignal } from "./types";
import { computeCostKpiSignal, type CostToleranceConfig } from "./budget";
import {
  computeFinanceCategoryFromInstances,
  type InstanceScore,
} from "./instanceScoring";

// ── Config reads ─────────────────────────────────────────────

/**
 * Read the SubKpiInstance[] config for a project's Finance/Budget category
 * from localStorage.
 */
export function getFinanceInstancesFromStorage(
  projectGuid: string,
): SubKpiInstance[] {
  const pg = String(projectGuid || "").trim();
  if (!pg) return [];
  const cfg = getProjectKpiConfigOverride(pg as any);
  return Array.isArray(cfg?.category_instances?.finance)
    ? cfg!.category_instances!.finance!
    : [];
}

// ── Data reads ───────────────────────────────────────────────

/**
 * Read cost data series for a single instance from localStorage.
 */
export function getFinanceInstanceSeriesFromStorage(
  projectGuid: string,
  instanceGuid?: string,
): FinanceCostPoint[] {
  return getFinanceCostSeriesForInstance(projectGuid, instanceGuid);
}

/**
 * Read curve metadata for a single instance from localStorage.
 */
export function getFinanceInstanceCurveMetaFromStorage(
  projectGuid: string,
  instanceGuid?: string,
): BudgetCurveMeta | null {
  return getBudgetCurveMetaForInstance(projectGuid, instanceGuid) ?? null;
}

/**
 * Build a Map<instanceGuid, FinanceCostPoint[]> for all instances
 * in a project's Finance category.
 */
export function buildBudgetSeriesMapFromStorage(
  projectGuid: string,
  instances: SubKpiInstance[],
): Map<string, FinanceCostPoint[]> {
  const map = new Map<string, FinanceCostPoint[]>();
  for (const inst of instances) {
    const key = String(inst.instance_guid || "").trim();
    const series = getFinanceCostSeriesForInstance(
      projectGuid,
      key || undefined,
    );
    map.set(key, series);
  }
  return map;
}

// ── Composite: read + score ──────────────────────────────────

/**
 * Read config and data from localStorage, then run the pure scoring pipeline.
 */
export function computeFinanceCategoryFromStorage(
  projectGuid: string,
  asOfDate: string,
  globalFinanceConfig: CostToleranceConfig,
): { signal: KpiSignal; instanceScores: InstanceScore[] } | null {
  const instances = getFinanceInstancesFromStorage(projectGuid);
  if (instances.length === 0) return null;

  const seriesMap = buildBudgetSeriesMapFromStorage(projectGuid, instances);

  // Guard: if instance configs exist but NO series data has been loaded into
  // the store yet (hydration timing race), return null so the caller falls
  // through to the API-data scoring path or the bundle fallback.
  let hasAnyPoints = false;
  seriesMap.forEach((pts) => { if (pts.length > 0) hasAnyPoints = true; });
  if (!hasAnyPoints) return null;

  return computeFinanceCategoryFromInstances(
    projectGuid,
    asOfDate,
    globalFinanceConfig,
    instances,
    seriesMap,
  );
}

// ── Fallback: project-level scoring (no instances required) ──

/**
 * Score Finance for a project that has cost data in localStorage but no
 * SubKpiInstance config.  Reads the project-level series (no instance_guid)
 * and runs the core cost scoring engine directly.
 *
 * Returns a KpiSignal (no instanceScores) or null when no data exists.
 */
export function computeFinanceFallbackFromStorage(
  projectGuid: string,
  asOfDate: string,
  globalFinanceConfig: CostToleranceConfig,
): KpiSignal | null {
  const pg = String(projectGuid || "").trim();
  if (!pg) return null;

  // Try project-level first, then scan all instance keys for this project.
  let series = getFinanceCostSeries(pg);
  if (!series.length) {
    series = getAllFinanceCostSeriesForProject(pg);
  }
  if (!series.length) return null;

  // Convert FinanceCostPoint → CostPoint shape expected by the scoring engine.
  const costPoints = series
    .filter((p) => p.actual_spend !== null && p.actual_spend !== undefined)
    .map((p) => ({
      project_GUID: pg,
      reporting_date: p.reporting_date,
      baseline_budget: Number(p.baseline_budget ?? 0),
      actual_spend: Number(p.actual_spend ?? 0),
      forecast_spend: p.forecast_spend != null ? Number(p.forecast_spend) : undefined,
    }));

  if (!costPoints.length) return null;

  return computeCostKpiSignal(costPoints, asOfDate, globalFinanceConfig);
}

// ── Data-driven: pre-fetched instances + series (no localStorage) ──

/**
 * Score a project's Finance category using PRE-FETCHED data from the API.
 *
 * This is the API-first path, mirroring computeScheduleCategoryFromData().
 * When the API provides KPI config instances and finance series data for a
 * project, App.tsx calls this instead of computeFinanceCategoryFromStorage().
 *
 * @param projectGuid     The project being scored.
 * @param asOfDate        Reporting cutoff (YYYY-MM-DD).
 * @param globalConfig    Platform-wide finance/cost thresholds (kpiConfig.finance).
 * @param instances       Pre-fetched SubKpiInstance[] from the API.
 * @param allFinanceData  All finance points in seed shape (project_GUID, etc.).
 */
export function computeFinanceCategoryFromData(
  projectGuid: string,
  asOfDate: string,
  globalConfig: CostToleranceConfig,
  instances: SubKpiInstance[],
  allFinanceData: Record<string, any>[],
): { signal: KpiSignal; instanceScores: InstanceScore[] } | null {
  if (!instances.length) return null;

  // Build seriesMap from the pre-fetched flat array.
  // Case-insensitive GUID comparison (CLAUDE.md convention).
  const pgUpper = projectGuid.toUpperCase();
  const projectPoints = allFinanceData.filter(
    (r) => String(r.project_GUID || "").trim().toUpperCase() === pgUpper
  );

  const seriesMap = new Map<string, FinanceCostPoint[]>();
  for (const inst of instances) {
    const iGuid = String(inst.instance_guid || "").trim();
    // Look for instance-specific points first
    const instPoints: FinanceCostPoint[] = projectPoints
      .filter((r) => String(r.instance_guid || "").trim() === iGuid)
      .map((r) => ({
        project_guid: projectGuid,
        instance_guid: iGuid || undefined,
        reporting_date: String(r.reporting_date || ""),
        baseline_budget: r.baseline_budget ?? null,
        actual_spend: r.actual_spend ?? null,
        forecast_spend: r.forecast_spend ?? null,
      }));

    if (instPoints.length > 0) {
      seriesMap.set(iGuid, instPoints);
    } else {
      // Fallback: use project-level points (instance_guid is empty/undefined)
      const projLevel: FinanceCostPoint[] = projectPoints
        .filter((r) => !String(r.instance_guid || "").trim())
        .map((r) => ({
          project_guid: projectGuid,
          instance_guid: undefined,
          reporting_date: String(r.reporting_date || ""),
          baseline_budget: r.baseline_budget ?? null,
          actual_spend: r.actual_spend ?? null,
          forecast_spend: r.forecast_spend ?? null,
        }));
      seriesMap.set(iGuid, projLevel);
    }
  }

  return computeFinanceCategoryFromInstances(
    projectGuid,
    asOfDate,
    globalConfig,
    instances,
    seriesMap,
  );
}

/**
 * Score Finance directly from API data WITHOUT requiring SubKpiInstance config.
 * Runs the core cost scoring engine on all finance points for a project.
 * Used as a fallback when no KPI instances are configured but API data exists.
 */
export function computeFinanceFallbackFromData(
  projectGuid: string,
  asOfDate: string,
  globalConfig: CostToleranceConfig,
  allFinanceData: Record<string, any>[],
): KpiSignal | null {
  const pg = String(projectGuid || "").trim().toUpperCase();
  if (!pg) return null;

  const projectPoints = allFinanceData.filter(
    (r) => String(r.project_GUID || "").trim().toUpperCase() === pg
  );
  if (!projectPoints.length) return null;

  const costPoints = projectPoints
    .filter((p) => p.actual_spend !== null && p.actual_spend !== undefined)
    .map((p) => ({
      project_GUID: pg,
      reporting_date: String(p.reporting_date || ""),
      baseline_budget: Number(p.baseline_budget ?? 0),
      actual_spend: Number(p.actual_spend ?? 0),
      forecast_spend: p.forecast_spend != null ? Number(p.forecast_spend) : undefined,
    }));

  if (!costPoints.length) return null;
  return computeCostKpiSignal(costPoints, asOfDate, globalConfig);
}

// ── Chart data bundle ────────────────────────────────────────

export type FinanceInstanceChartData = {
  series: FinanceCostPoint[];
  curveMeta: BudgetCurveMeta | null;
};

/**
 * Read all data needed by a chart component for a single finance instance.
 */
export function getFinanceInstanceChartDataFromStorage(
  projectGuid: string,
  instanceGuid?: string,
): FinanceInstanceChartData {
  return {
    series: getFinanceInstanceSeriesFromStorage(projectGuid, instanceGuid),
    curveMeta: getFinanceInstanceCurveMetaFromStorage(projectGuid, instanceGuid),
  };
}
